import { useCallback, useEffect, useState, useRef } from 'react';
import { useInView } from 'react-intersection-observer';
import { EMPLOYEES_COUNT, PROJECTS_COUNT } from 'define';
import { useOfficesCount } from 'staticQueries/offices-count';

export const useTooltipState = (dependencies = []) => {
  const [isOpen, setIsOpen] = useState(false);

  const showTooltip = useCallback(() => setIsOpen(true), dependencies);
  const hideTooltip = useCallback(() => setIsOpen(false), dependencies);

  return { isOpen, showTooltip, hideTooltip };
};

export const useStateModal = () => {
  const focusRef = useRef(null);
  const [modalState, setModalState] = useState({ isOpen: false });

  const showModal = useCallback(e => {
    focusRef.current = e?.currentTarget ?? null;
    setModalState({ isOpen: true });
  }, []);

  const hideModal = useCallback(() => {
    setModalState({ isOpen: false });
    focusRef.current?.focus();
  }, []);

  return { modalState, showModal, hideModal };
};

export const useStateModals = () => {
  const focusRef = useRef(null);
  const [modalState, setModalState] = useState({});

  const showModal = useCallback((key, e) => {
    focusRef.current = e?.currentTarget ?? null;
    setModalState(prev => ({ ...prev, [key]: true }));
  }, []);

  const hideModal = useCallback(
    key => () => {
      setModalState(prev => ({ ...prev, [key]: false }));
      focusRef.current?.focus();
    },
    [],
  );

  return { modalState, showModal, hideModal };
};

export function useLoadingComponent(ApiCall, ...args) {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);

  useEffect(() => {
    ApiCall(...args)
      .then(setData)
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, []);

  return { data, loading };
}

export function useDefferedCallback(callback, options = {}) {
  const defaultDelay = 5000;
  const { delay, rootMargin, initialInView } = options;
  const preparedDelay = delay ?? defaultDelay;

  const [triggered, setTriggered] = useState(false);
  const [inViewRef, inView] = useInView({
    triggerOnce: true,
    rootMargin,
    initialInView,
  });

  const callbackRef = useRef(callback);
  callbackRef.current = callback;

  useEffect(() => {
    if (triggered) return;

    const trigger = () => {
      callbackRef.current();
      setTriggered(true);
    };

    if (inView) {
      trigger();
      return;
    }

    let timer;
    const schedule = () => {
      timer = setTimeout(trigger, preparedDelay);
    };

    if (document.readyState === 'complete') {
      schedule();
    } else {
      window.addEventListener('load', schedule, { once: true });
    }

    return () => {
      clearTimeout(timer);
      window.removeEventListener('load', schedule);
    };
  }, [inView, triggered, preparedDelay]);

  return { inViewRef };
}

export const useClickOutside = (refs, handler) => {
  useEffect(() => {
    const targets = refs.map(el => el.current);

    const listener = e => {
      if (
        !targets.some(el => el?.contains && el.contains(e.target)) &&
        typeof handler === 'function'
      ) {
        handler();
      }
    };

    document.addEventListener('mouseup', listener);
    document.addEventListener('touchend', listener);
    return () => {
      document.removeEventListener('mouseup', listener);
      document.removeEventListener('touchend', listener);
    };
  }, [refs, handler]);
};

export const useWindowResize = () => {
  const [width, setWidth] = useState(null);

  useEffect(() => {
    function handleResize() {
      setWidth(window.innerWidth);
    }
    handleResize();

    window.addEventListener('resize', handleResize);

    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return { width };
};

export const useGetDeviceType = (breakpoint = 545) => {
  const [deviceType, setDeviceType] = useState('Desktop');

  useEffect(() => {
    const mediaQuery = window.matchMedia(`(min-width: ${breakpoint + 1}px)`);

    const windowResizeHandler = () => {
      setDeviceType(window.innerWidth >= breakpoint ? 'Desktop' : 'Mobile');
    };

    const changeWindowHandler = event => {
      setDeviceType(event.matches ? 'Desktop' : 'Mobile');
    };

    if (mediaQuery?.addEventListener) {
      mediaQuery.addEventListener('change', changeWindowHandler);
      setDeviceType(mediaQuery.matches ? 'Desktop' : 'Mobile');
    } else {
      window.addEventListener('resize', windowResizeHandler);
      windowResizeHandler();
    }

    return () => {
      if (mediaQuery.addEventListener) {
        mediaQuery.removeEventListener('change', changeWindowHandler);
      } else {
        window.removeEventListener('resize', windowResizeHandler);
      }
    };
  }, [breakpoint]);

  return deviceType;
};

export function useHorizontalScroll(deps) {
  const scrollRef = useRef();
  const [scrollLeft, setScrollLeft] = useState(null);

  useEffect(() => {
    const el = scrollRef.current;

    if (el) {
      const onWheel = e => {
        const scrollIsNotNeeded = el.clientWidth === el.scrollWidth;
        setScrollLeft(el.scrollLeft);

        if (e.deltaY === 0) return;
        if (scrollIsNotNeeded) return;

        e.preventDefault();

        el.scrollTo({
          left: el.scrollLeft + e.deltaY,
        });
      };

      el.addEventListener('wheel', onWheel);

      return () => {
        el.removeEventListener('wheel', onWheel);
      };
    }
  }, [deps]);

  return { scrollRef, scrollLeft, setScrollLeft };
}

export const useReplaceKeyword = () => {
  const officesCount = useOfficesCount();

  const checkKeyword = match => {
    const companyAge = new Date().getFullYear() - new Date(2007, 1, 1).getFullYear();

    switch (match) {
      case '{age}':
        return companyAge;
      case '{employees}':
        return EMPLOYEES_COUNT;
      case '{projects}':
        return PROJECTS_COUNT;
      case '{offices}':
        return officesCount;
      default:
        return match;
    }
  };

  const replaceKeyword = (str, specialKey, data) => {
    if (typeof str !== 'string') return str;

    const matches = str.matchAll(/{(\w+)}/g);
    let result = str;

    for (const match of matches) {
      if (specialKey && match[1] === specialKey) {
        result = result.replace(match[0], data);
      } else {
        result = result.replace(match[0], checkKeyword);
      }
    }
    return result;
  };

  return { replaceKeyword };
};

export const useSsr = () => {
  // we always start off in "SSR mode", to ensure our initial browser render matches the SSR render
  const [isSsr, setIsSsr] = useState(true);

  useEffect(() => {
    // `useEffect` never runs on the server, so we must be on the client if we hit this block
    setIsSsr(false);
  }, []);

  return isSsr;
};
